import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const ActiveVendors = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [sortBy, setSortBy] = useState('name');

  const vendors = [
    {
      id: 1,
      name: 'Beauty Essentials Co.',
      category: 'Skincare',
      status: 'active',
      joinedDate: '2024-06-15',
      totalProducts: 142,
      totalOrders: 1250,
      revenue: '$125,450',
      rating: 4.8,
      fulfillmentRate: 98,
      responseTime: '2 hours',
      commission: 15,
      lastActivity: '2025-11-19'
    },
    {
      id: 2,
      name: 'Glamour Makeup Studio',
      category: 'Makeup',
      status: 'active',
      joinedDate: '2024-08-20',
      totalProducts: 89,
      totalOrders: 856,
      revenue: '$89,320',
      rating: 4.6,
      fulfillmentRate: 95,
      responseTime: '4 hours',
      commission: 18,
      lastActivity: '2025-11-18'
    },
    {
      id: 3,
      name: 'Hair Care Experts',
      category: 'Hair Care',
      status: 'active',
      joinedDate: '2024-05-10',
      totalProducts: 267,
      totalOrders: 2134,
      revenue: '$215,680',
      rating: 4.9,
      fulfillmentRate: 99,
      responseTime: '1 hour',
      commission: 12,
      lastActivity: '2025-11-19'
    },
    {
      id: 4,
      name: 'Organic Beauty Labs',
      category: 'Skincare',
      status: 'warning',
      joinedDate: '2024-09-05',
      totalProducts: 56,
      totalOrders: 423,
      revenue: '$42,150',
      rating: 4.2,
      fulfillmentRate: 87,
      responseTime: '8 hours',
      commission: 20,
      lastActivity: '2025-11-15'
    },
    {
      id: 5,
      name: 'Luxury Fragrance House',
      category: 'Fragrance',
      status: 'active',
      joinedDate: '2024-07-12',
      totalProducts: 78,
      totalOrders: 645,
      revenue: '$98,750',
      rating: 4.7,
      fulfillmentRate: 96,
      responseTime: '3 hours',
      commission: 16,
      lastActivity: '2025-11-19'
    }
  ];

  const categories = ['all', 'Skincare', 'Makeup', 'Hair Care', 'Fragrance'];

  const getStatusConfig = (status) => {
    const configs = {
      active: { label: 'Active', color: 'text-success', bgColor: 'bg-success/10', icon: 'CheckCircle2' },
      warning: { label: 'Needs Attention', color: 'text-warning', bgColor: 'bg-warning/10', icon: 'AlertTriangle' },
      suspended: { label: 'Suspended', color: 'text-error', bgColor: 'bg-error/10', icon: 'Ban' }
    };
    return configs?.[status] || configs?.active;
  };

  const getPerformanceBadge = (rate) => {
    if (rate >= 95) return { label: 'Excellent', color: 'text-success bg-success/10' };
    if (rate >= 85) return { label: 'Good', color: 'text-info bg-info/10' };
    return { label: 'Needs Improvement', color: 'text-warning bg-warning/10' };
  };

  const filteredVendors = vendors
    ?.filter(vendor => {
      const matchesSearch = vendor?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
                           vendor?.category?.toLowerCase()?.includes(searchQuery?.toLowerCase());
      const matchesCategory = filterCategory === 'all' || vendor?.category === filterCategory;
      return matchesSearch && matchesCategory;
    })
    ?.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a?.name?.localeCompare(b?.name);
        case 'revenue':
          return parseFloat(b?.revenue?.replace(/[$,]/g, '')) - parseFloat(a?.revenue?.replace(/[$,]/g, ''));
        case 'orders':
          return b?.totalOrders - a?.totalOrders;
        case 'rating':
          return b?.rating - a?.rating;
        default:
          return 0;
      }
    });

  return (
    <div className="space-y-6">
      {/* Filters and Controls */}
      <div className="bg-card rounded-xl border border-border p-6">
        <div className="space-y-4">
          {/* Search and Sort */}
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1">
              <Input
                placeholder="Search vendors by name or category..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e?.target?.value)}
                leftIcon="Search"
              />
            </div>
            
            <div className="flex gap-2">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e?.target?.value)}
                className="px-4 py-2 rounded-lg border border-border bg-background text-foreground text-sm font-medium hover:bg-muted/50 transition-colors focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="name">Sort by Name</option>
                <option value="revenue">Sort by Revenue</option>
                <option value="orders">Sort by Orders</option>
                <option value="rating">Sort by Rating</option>
              </select>
            </div>
          </div>

          {/* Category Filters */}
          <div className="flex flex-wrap gap-2">
            {categories?.map((category) => (
              <Button
                key={category}
                variant={filterCategory === category ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilterCategory(category)}
              >
                {category === 'all' ? 'All Categories' : category}
              </Button>
            ))}
          </div>
        </div>
      </div>

      {/* Vendors Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {filteredVendors?.map((vendor) => {
          const statusConfig = getStatusConfig(vendor?.status);
          const performanceBadge = getPerformanceBadge(vendor?.fulfillmentRate);
          
          return (
            <div
              key={vendor?.id}
              className="bg-card rounded-xl border border-border p-6 hover:shadow-elevation-2 transition-shadow"
            >
              {/* Vendor Header */}
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-lg font-heading font-semibold text-foreground">
                    {vendor?.name}
                  </h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    {vendor?.category} • Joined {new Date(vendor?.joinedDate)?.toLocaleDateString()}
                  </p>
                </div>
                
                <div className={`px-3 py-1.5 rounded-full flex items-center gap-2 ${statusConfig?.bgColor}`}>
                  <Icon name={statusConfig?.icon} size={12} className={statusConfig?.color} />
                  <span className={`text-xs font-medium ${statusConfig?.color}`}>
                    {statusConfig?.label}
                  </span>
                </div>
              </div>

              {/* Metrics Grid */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground">Products</p>
                  <p className="text-lg font-semibold text-foreground">{vendor?.totalProducts}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground">Orders</p>
                  <p className="text-lg font-semibold text-foreground">{vendor?.totalOrders?.toLocaleString()}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground">Revenue</p>
                  <p className="text-lg font-semibold text-success">{vendor?.revenue}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground">Rating</p>
                  <div className="flex items-center gap-1">
                    <Icon name="Star" size={14} className="text-warning fill-warning" />
                    <p className="text-lg font-semibold text-foreground">{vendor?.rating}</p>
                  </div>
                </div>
              </div>

              {/* Performance Indicators */}
              <div className="space-y-3 mb-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Fulfillment Rate</span>
                  <div className="flex items-center gap-2">
                    <div className="w-24 h-2 bg-muted rounded-full overflow-hidden">
                      <div 
                        className={`h-full ${vendor?.fulfillmentRate >= 95 ? 'bg-success' : vendor?.fulfillmentRate >= 85 ? 'bg-info' : 'bg-warning'}`}
                        style={{ width: `${vendor?.fulfillmentRate}%` }}
                      />
                    </div>
                    <span className="text-sm font-medium text-foreground">{vendor?.fulfillmentRate}%</span>
                  </div>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Response Time</span>
                  <span className="text-sm font-medium text-foreground">{vendor?.responseTime}</span>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Commission</span>
                  <span className="text-sm font-medium text-foreground">{vendor?.commission}%</span>
                </div>
              </div>

              {/* Performance Badge */}
              <div className={`px-3 py-1.5 rounded-lg text-xs font-medium text-center mb-4 ${performanceBadge?.color}`}>
                {performanceBadge?.label}
              </div>

              {/* Actions */}
              <div className="flex gap-2">
                <Button variant="outline" size="sm" fullWidth iconName="Eye" iconPosition="left">
                  View Details
                </Button>
                <Button variant="outline" size="sm" fullWidth iconName="MessageSquare" iconPosition="left">
                  Message
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      {filteredVendors?.length === 0 && (
        <div className="bg-card rounded-xl border border-border p-12 text-center">
          <Icon name="Building2" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-foreground mb-2">
            No vendors found
          </h3>
          <p className="text-muted-foreground">
            Try adjusting your search or filter criteria
          </p>
        </div>
      )}
    </div>
  );
};

export default ActiveVendors;